import React, { useState, useContext, useEffect, useRef } from "react";
import { useSiteConfig } from "../context/SiteConfigContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CartCanvas from "../pages/canvas/CartCanvas";
import Auth from "../utils/AuthUtils";
import { useCart } from "../context/CartContext";
import { CategoryContext } from "../context/CategoryContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  User,
  Search,
  Heart,
  ShoppingBag,
  X,
  ChevronDown,
} from "lucide-react";
import SearchCanvas from "../pages/canvas/SearchCanvas";

/* ------------------------------------------------------------------
    Signature mark — a mandala ring echoing the ANNVANTA logo motif.
------------------------------------------------------------------- */
function MandalaMark({ size = 30 }) {
  return (
    <span
      className="relative flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <motion.svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        className="absolute inset-0"
        animate={{ rotate: 360 }}
        transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
      >
        <circle
          cx="20" cy="20" r="17.5"
          fill="none"
          stroke="var(--color-accent-gold)"
          strokeWidth="1"
          strokeDasharray="2.4 3.2"
          opacity="0.55"
        />
        <circle
          cx="20" cy="20" r="12.5"
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="1"
          strokeDasharray="1.2 2.8"
          opacity="0.4"
        />
      </motion.svg>
      <span
        className="w-2 h-2 rounded-full shadow-sm"
        style={{ background: "var(--color-primary)" }}
      />
    </span>
  );
}

/* Small ornamental diamond divider */
function Pip() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-1 h-1 rotate-45 mx-0.5 shrink-0"
      style={{ background: "var(--color-accent-gold)", opacity: 0.7 }}
    />
  );
}

function Header() {
  const [searchText, setSearchText] = useState("");
  const { cartItems } = useCart();
  const { config } = useSiteConfig();
  const isLoggedIn = Auth.getToken();
  const { categories } = useContext(CategoryContext);
  const [scrolled, setScrolled] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const mobileHeaderRef = useRef(null);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = searchOpen || menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [searchOpen, menuOpen]);

  useEffect(() => {
    const el = mobileHeaderRef.current;
    if (!el) return;
    const updateHeight = () => setMobileHeaderHeight(el.offsetHeight);
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  if (!config) return null;

  /* Responsive classes: tighter spacing specifically targeted for 1024px (via max-xl), leaving 1440px identical */
  const navLinkClass =
    "px-1.5 xl:px-2.5 py-1 text-[11px] xl:text-xs font-semibold uppercase tracking-[0.1em] xl:tracking-[0.12em] transition-all duration-300 whitespace-nowrap relative group hover:text-[var(--color-primary)]";

  return (
    <>
      {/* ==============================================
          DESKTOP HEADER — Luxury Floating Container
      =============================================== */}
      <header className="hidden lg:flex fixed top-3 left-0 right-0 z-50 justify-center px-4 xl:px-6">
        <div
          className="w-full max-w-7xl flex items-center justify-between gap-2 xl:gap-4 px-3.5 xl:px-5 py-2 border transition-all duration-500 rounded-[18px]"
          style={{
            background: scrolled ? "rgba(255,253,248,0.98)" : "rgba(255,253,248,0.92)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            borderColor: scrolled ? "var(--color-primary-light)" : "var(--color-border)",
            boxShadow: scrolled ? "0 10px 30px -10px rgba(0,0,0,0.08)" : "0 4px 20px -4px rgba(0,0,0,0.05)",
          }}
        >
          {/* Brand / Logo */}
          <Link to="/" className="flex items-center gap-1.5 xl:gap-2 group py-0.5 shrink-0">
            {config?.logo ? (
              <div className="flex items-center">
                <img
                  src={config.logo}
                  alt="ANNVANTA Logo"
                  className="custom-header-logo object-contain transition-transform duration-300 group-hover:scale-105 filter drop-shadow-sm"
                />
              </div>
            ) : (
              <>
                <MandalaMark />
                <h1
                  className="text-lg xl:text-xl tracking-[0.1em] leading-none font-bold"
                  style={{ fontFamily: "var(--font-heading)", color: "var(--color-text)" }}
                >
                  ANN<span style={{ color: "var(--color-primary)" }}>VANTA</span>
                </h1>
              </>
            )}
          </Link>

          {/* Nav Links */}
          <nav className="flex items-center px-1 xl:px-2 py-1 gap-0.5 xl:gap-1 shrink">
            <Link
              to="/"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              Home
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>
            <Link
              to="/about-us"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/about-us" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/about-us" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              About
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/about-us" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>

            {/* {categories?.map((cat) => {
              const isCatActive = location.pathname.includes(`/categories/${cat.slugPath}`);
              return (
                <React.Fragment key={cat._id}>
                  <div
                    className="relative"
                    onMouseEnter={() => setActiveDropdown(cat._id)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={`/categories/${cat.slugPath}`}
                      className={`${navLinkClass} custom-nav-link flex items-center gap-0.5 ${location.pathname === "/" ? "text-[var(--color-primary)] font-bold" : ""}`}
                      style={{ color: isCatActive ? "var(--color-primary)" : "var(--color-text)" }}
                    >
                      {cat.name}
                      {cat.children?.length > 0 && (
                        <span>
                          <ChevronDown
                            size={12}
                            className="transition-transform duration-300"
                            style={{ transform: activeDropdown === cat._id ? "rotate(180deg)" : "rotate(0deg)" }}
                          />
                        </span>
                      )}
                      <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${isCatActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
                    </Link>

                    <AnimatePresence>
                      {activeDropdown === cat._id && cat.children?.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 4, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                          className="absolute top-full left-0 mt-2 w-52 rounded-2xl border p-2 z-50"
                          style={{
                            background: "var(--color-bg)",
                            borderColor: "var(--color-border)",
                            boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
                          }}
                        >
                          <div className="h-[2px] w-6 mb-1.5 mx-2 rounded-full" style={{ background: "var(--color-accent-gold)" }} />
                          {cat.children.map((sub) => (
                            <Link
                              key={sub._id}
                              to={`/categories/${sub.slugPath}`}
                              className="block px-3 py-1.5 text-xs font-medium rounded-xl transition-colors hover:bg-[var(--color-bg-alt)] hover:text-[var(--color-primary)]"
                              style={{ color: "var(--color-text-muted)" }}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </React.Fragment>
              );
            })} */}


            {/* {categories?.map((cat) => {
              const isCatActive = location.pathname.includes(
                `/categories/${cat.slugPath}`
              );

              const hasSubcategories = cat.children?.length > 0;

              return (
                <div
                  key={cat._id}
                  className="relative h-full"
                >
                  

                  {hasSubcategories ? (
                   
                    <button
                      type="button"
                      onClick={() => {
                        setActiveDropdown((prev) =>
                          prev === cat._id ? null : cat._id
                        );
                      }}
                      className={`${navLinkClass} custom-nav-link flex items-center gap-1 relative`}
                      style={{
                        color: isCatActive
                          ? "var(--color-primary)"
                          : "var(--color-text)",
                      }}
                    >
                      {cat.name}

                      
                      <ChevronDown
                        size={12}
                        className="transition-transform duration-300"
                        style={{
                          transform:
                            activeDropdown === cat._id
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                        }}
                      />

                      
                      <span
                        className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${isCatActive
                            ? "scale-x-100"
                            : "scale-x-0"
                          }`}
                      />
                    </button>
                  ) : (
                   
                    <Link
                      to={`/categories/${cat.slugPath}`}
                      className={`${navLinkClass} custom-nav-link flex items-center gap-0.5 relative`}
                      style={{
                        color: isCatActive
                          ? "var(--color-primary)"
                          : "var(--color-text)",
                      }}
                    >
                      {cat.name}

                      
                      <span
                        className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${isCatActive
                            ? "scale-x-100"
                            : "scale-x-0"
                          }`}
                      />
                    </Link>
                  )}

               

                  <AnimatePresence>
                    {activeDropdown === cat._id &&
                      hasSubcategories && (
                        <motion.div
                          initial={{
                            opacity: 0,
                            y: 8,
                            scale: 0.97,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                          }}
                          exit={{
                            opacity: 0,
                            y: 4,
                            scale: 0.97,
                          }}
                          transition={{
                            duration: 0.18,
                            ease: "easeOut",
                          }}
                          className="absolute top-full left-0 mt-2 w-52 rounded-2xl border p-2 z-50"
                          style={{
                            background: "var(--color-bg)",
                            borderColor: "var(--color-border)",
                            boxShadow:
                              "0 12px 32px rgba(0,0,0,0.08)",
                          }}
                        >
                          
                          <div
                            className="h-[2px] w-6 mb-2 mx-2 rounded-full"
                            style={{
                              background:
                                "var(--color-accent-gold)",
                            }}
                          />

                        

                          {cat.children.map((sub) => (
                            <Link
                              key={sub._id}
                              to={`/categories/${sub.slugPath}`}
                              onClick={() => setActiveDropdown(null)}
                              className="block px-3 py-2 text-xs font-medium rounded-xl transition-colors hover:bg-[var(--color-bg-alt)] hover:text-[var(--color-primary)]"
                              style={{
                                color: "var(--color-text-muted)",
                              }}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>
              );
            })} */}
            
            <Link
              to="/shop"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/shop" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/shop" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              Shop
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/shop" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>
            <Link
              to="/blog"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/blog" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/blog" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              Blog
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/blog" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>
            <Link
              to="/contact-us"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/contact-us" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/contact-us" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              Contact
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/contact-us" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>


              <Link
              to="/bulk-inquiry"
              className={`${navLinkClass} custom-nav-link ${location.pathname === "/contact-us" ? "text-[var(--color-primary)] font-bold" : ""}`}
              style={{ color: location.pathname === "/bulk-inquiry" ? "var(--color-primary)" : "var(--color-text)" }}
            >
              Bulk Inquiry
              <span className={`absolute bottom-0 left-1.5 xl:left-2.5 right-1.5 xl:right-2.5 h-[2px] bg-[var(--color-primary)] transition-transform origin-left duration-300 ${location.pathname === "/contact-us" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}></span>
            </Link>


          </nav>

          {/* Utility icons */}
          <div className="flex items-center gap-1.5 xl:gap-2 shrink-0" ref={profileRef}>
            <div
              className="group relative flex items-center w-28 xl:w-44 px-2.5 xl:px-4 py-1.5 xl:py-2 rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5"
              style={{
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
              }}
            >
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setShowSearchModal(true);
                }}
                onFocus={() => setShowSearchModal(true)}
                placeholder="Search..."
                className="w-full bg-transparent border-0 shadow-none text-[10px] xl:text-xs font-medium pe-4"
                style={{
                  fontFamily: "var(--font-body)",
                  color: "var(--color-text)",
                  outline: "none",
                }}
              />

              <Search
                size={15}
                strokeWidth={2.2}
                className="absolute right-2 xl:right-3 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  color: "var(--color-secondary)",
                  pointerEvents: "none",
                }}
              />

              <SearchCanvas
                isOpen={showSearchModal}
                searchText={searchText}
                setSearchText={setSearchText}
                onClose={() => setShowSearchModal(false)}
              />
            </div>

            <Link
              to="/wishlist"
              className="p-2 rounded-xl border transition-all duration-300 flex items-center justify-center shrink-0"
              style={{ background: "var(--color-bg-alt)", color: "var(--color-text)", borderColor: "var(--color-border)" }}
              title="Wishlist"
            >
              <Heart size={16} strokeWidth={2} />
            </Link>

            {/* Profile Dropdown */}
            <div className="relative shrink-0">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setProfileOpen(!profileOpen)}
                className="p-2 rounded-xl border transition-all duration-300 cursor-pointer flex items-center justify-center"
                style={{
                  background: profileOpen ? "var(--color-primary)" : "var(--color-bg-alt)",
                  color: profileOpen ? "#fff" : "var(--color-text)",
                  borderColor: "var(--color-border)",
                }}
              >
                <User size={16} strokeWidth={2} />
              </motion.button>

              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.97 }}
                    transition={{ duration: 0.18 }}
                    className="absolute top-full right-0 mt-2 w-56 rounded-2xl border p-2.5 z-50 shadow-xl"
                    style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}
                  >
                    <div className="px-3 py-2 border-b mb-1" style={{ borderColor: "var(--color-border)" }}>
                      <p className="text-xs font-bold" style={{ color: "var(--color-text)" }}>
                        {isLoggedIn ? "My Account" : "Welcome"}
                      </p>
                    </div>
                    <Link
                      to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                      onClick={() => setProfileOpen(false)}
                      className="custom-profile-menu-link flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors text-xs font-medium hover:bg-[var(--color-primary)]"
                    >
                      <User size={14} />
                      {isLoggedIn ? "Dashboard" : "Login / Register"}
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Cart Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-bs-toggle="offcanvas"
              data-bs-target="#shoppingCartCanvas"
              className="relative p-2 rounded-xl transition-all duration-300 cursor-pointer flex items-center justify-center text-white shadow-md shrink-0"
              style={{ background: "var(--color-primary)" }}
            >
              <ShoppingBag size={16} strokeWidth={2} />
              {cartItems.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 text-[9px] font-extrabold flex items-center justify-center rounded-full border-2 shadow"
                  style={{ background: "var(--color-accent-gold)", color: "#fff", borderColor: "var(--color-bg)" }}
                >
                  {cartItems.length}
                </span>
              )}
            </motion.button>
          </div>
        </div>
      </header>

      {/* ==============================================
          MOBILE / TABLET HEADER
      =============================================== */}
      <div
        ref={mobileHeaderRef}
        className="lg:hidden fixed top-0 left-0 right-0 z-50 flex flex-col transition-all duration-300"
        style={{
          background: scrolled ? "rgba(255,253,248,0.98)" : "rgba(255,253,248,0.88)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: scrolled ? "1px solid var(--color-border)" : "1px solid transparent",
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.04)" : "none",
        }}
      >
        <div className="px-3 py-2 sm:px-5 sm:py-3 flex justify-between items-center gap-2">
          <Link to="/" className="relative z-50 flex items-center gap-1.5 sm:gap-2 min-w-0 max-w-[50%] truncate">
            {config?.logo ? (
              <img src={config.logo} alt="ANNVANTA Logo" className="custom-header-logo object-contain" />
            ) : (
              <>
                <MandalaMark size={24} />
                <h1 className="text-sm sm:text-lg tracking-[0.08em] font-bold truncate" style={{ fontFamily: "var(--font-heading)", color: "var(--color-text)" }}>
                  ANN<span style={{ color: "var(--color-primary)" }}>VANTA</span>
                </h1>
              </>
            )}
          </Link>

          <div className="flex items-center gap-1 relative z-50 shrink-0">
            <Link
              to="/wishlist"
              className="flex items-center justify-center p-[0.45rem] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-alt)] text-[var(--color-text)] transition-transform active:scale-95"
              title="Wishlist"
            >
              <Heart size={14} strokeWidth={2} />
            </Link>

            <Link
              to={isLoggedIn ? "/users/dashboard" : "/users/login"}
              className="flex items-center justify-center p-[0.45rem] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-alt)] text-[var(--color-text)] transition-transform active:scale-95"
              title={isLoggedIn ? "My Account" : "Login"}
            >
              <User size={14} strokeWidth={2} />
            </Link>

            <button
              data-bs-toggle="offcanvas"
              data-bs-target="#shoppingCartCanvas"
              className="relative flex items-center justify-center p-[0.45rem] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-alt)] text-[var(--color-text)] transition-transform active:scale-95"
              title="Cart"
            >
              <ShoppingBag size={14} strokeWidth={2} />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 text-[9px] font-bold flex items-center justify-center rounded-full text-white shadow bg-[var(--color-primary)]">
                  {cartItems.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center p-[0.45rem] rounded-xl text-white transition-transform active:scale-95 shadow-sm bg-[var(--color-primary)]"
              title="Menu"
            >
              <Menu size={14} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="px-3 pb-2">
          <div className="relative flex items-center border-b border-[var(--color-border)]">
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setShowSearchModal(true);
              }}
              onFocus={() => setShowSearchModal(true)}
              placeholder="Search..."
              className="w-full bg-transparent border-0 shadow-none outline-none py-2.5 pe-8 text-[0.95rem] text-[var(--color-text)]"
            />
            <Search size={20} strokeWidth={1.8} className="absolute right-1 text-[var(--color-text)] pointer-events-none" />
          </div>

          <SearchCanvas
            isOpen={showSearchModal}
            searchText={searchText}
            setSearchText={setSearchText}
            onClose={() => setShowSearchModal(false)}
          />
        </div>
      </div>

      {/* ==============================================
          MOBILE FULLSCREEN MENU
      =============================================== */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[100] flex flex-col p-6 overflow-y-auto"
            style={{ background: "var(--color-secondary)", color: "var(--color-tan-light)" }}
          >
            <div className="flex justify-between items-center mb-6 border-b pb-3" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rotate-45 shrink-0" style={{ background: "var(--color-accent-gold)" }} />
                <h2 className="text-lg tracking-[0.1em]" style={{ fontFamily: "var(--font-heading)", color: "#fff" }}>
                  Menu
                </h2>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-xl transition-all"
                style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
              >
                <X size={18} />
              </button>
            </div>

            <nav className="flex flex-col gap-3 flex-1 pb-8">
              <Link to="/" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                Home
              </Link>
              <Link to="/about-us" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/about-us" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/about-us" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                About Us
              </Link>

              {categories?.map((cat) => (
                <div key={cat._id} className="flex flex-col gap-2 border-b pb-3" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <Link
                    to={`/categories/${cat.slugPath}`}
                    onClick={() => setMenuOpen(false)}
                    className={`text-base tracking-wide ${location.pathname.includes(`/categories/${cat.slugPath}`) ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                    style={{ fontFamily: "var(--font-heading)", color: location.pathname.includes(`/categories/${cat.slugPath}`) ? "var(--color-accent-gold)" : "#fff" }}
                  >
                    {cat.name}
                  </Link>
                  {cat.children?.length > 0 && (
                    <div className="pl-4 flex flex-col gap-2 mt-1">
                      {cat.children.map((sub) => (
                        <Link
                          key={sub._id}
                          to={`/categories/${sub.slugPath}`}
                          onClick={() => setMenuOpen(false)}
                          className="text-xs transition-colors py-0.5 hover:text-white"
                          style={{ color: "var(--color-tan-light)" }}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <Link to="/shop" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/shop" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/shop" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                Shop
              </Link>
              <Link to="/blog" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/blog" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/blog" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                Blog
              </Link>
              <Link to="/contact-us" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/contact-us" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/contact-us" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                Contact Us
              </Link>
               <Link to="/bulk-inquiry" onClick={() => setMenuOpen(false)} className={`text-base tracking-wide border-b pb-3 ${location.pathname === "/contact-us" ? "text-[var(--color-accent-gold)] font-bold" : ""}`}
                style={{ fontFamily: "var(--font-heading)", color: location.pathname === "/contact-us" ? "var(--color-accent-gold)" : "#fff", borderColor: "rgba(255,255,255,0.1)" }}>
                Bulk Inquiry
              </Link>
              <Link
                to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                onClick={() => setMenuOpen(false)}
                className="text-base tracking-wide pt-2"
                style={{ fontFamily: "var(--font-heading)", color: "var(--color-accent-gold-light)" }}
              >
                {isLoggedIn ? "My Dashboard" : "Login / Register"}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer so page content clears the fixed header */}
      <div className="lg:hidden" style={{ height: mobileHeaderHeight }} aria-hidden="true" />
      <div className="hidden lg:block h-20" aria-hidden="true" />

      {/* Cart Offcanvas Container */}
      <div className="offcanvas offcanvas-end custom-cart-offcanvas" id="shoppingCartCanvas">
        <CartCanvas />
      </div>
    </>
  );
}

export default Header;